<script setup lang="ts">
import type { Ref } from 'vue'
import { computed, inject, onMounted, watch } from 'vue'
import {
  useSidebarStore,
  type TabName,
  ResolutionTab,
  SentencesTab,
  SpellCalculatorTab,
  CardsTab,
  DiceTab,
  RandomTablesTab,
} from '@tag-and-tally/shared-ui'
import logoUrl from '@data/images/logo.png'

// Name predates the Forge → Sparks rename; changing it would reset saved settings.
const STORAGE_KEY = 'tag-tally-forge-sidebar'

const store = useSidebarStore()
const pwaNeedRefresh = inject<Ref<boolean>>('pwaNeedRefresh')
const pwaReload = inject<() => void>('pwaReload', () => {})

const APP_TABS: { name: TabName; label: string; icon: string }[] = [
  { name: 'resolution', label: 'Resolution', icon: 'scale-balanced' },
  { name: 'sentences', label: 'Sentences', icon: 'scroll' },
  { name: 'spell', label: 'Spell', icon: 'wand-sparkles' },
  { name: 'cards', label: 'Cards', icon: 'cards' },
  { name: 'dice', label: 'Dice', icon: 'dice' },
  { name: 'tables', label: 'Random Tables', icon: 'th-list' },
]

const activeTab = computed(() => {
  const t = store.activeTab ?? 'resolution'
  return (t === 'travel' || t === 'map') ? 'resolution' : t
})

/* Web app is always light mode (not changeable) */
const themeClass = computed(() => 'theme-light')

/* Version from Vite define (build) or fallback in dev */
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'

function setActiveTab(name: TabName) {
  store.activeTab = name
}

onMounted(() => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const data = raw ? JSON.parse(raw) : null
    store.hydrate(data)
    /* Force light theme; web app does not support dark mode */
    store.theme = 'theme-light'
  } catch {
    // ignore
  }
})

watch(
  themeClass,
  (value) => {
    if (typeof document === 'undefined') return
    document.body.classList.remove('theme-light', 'theme-dark')
    document.body.classList.add(value)
  },
  { immediate: true }
)

let persistTimeout: ReturnType<typeof setTimeout> | null = null
function persist() {
  const data = {
    activeTab: store.activeTab,
    hexInputValue: store.hexInputValue,
    theme: store.theme,
    sentencesTheme: store.sentencesTheme,
    selectedSentenceType: store.selectedSentenceType,
    locationType: store.locationType,
    useFantasySpecies: store.useFantasySpecies,
    cardsState: store.cardsState ?? undefined,
    diceState: store.diceState ?? undefined,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

watch(
  () => ({ ...store.$state }),
  () => {
    if (persistTimeout) clearTimeout(persistTimeout)
    persistTimeout = setTimeout(() => {
      persistTimeout = null
      persist()
    }, 300)
  },
  { deep: true }
)
</script>

<template>
  <div class="app-wrap" :class="themeClass">
    <div v-if="pwaNeedRefresh?.value" class="pwa-update-banner" role="alert">
      <span class="pwa-update-banner__text">New version available.</span>
      <button type="button" class="pwa-update-banner__btn" @click="pwaReload">Refresh</button>
    </div>
    <div class="app">
      <header class="sparks-header">
        <a class="sparks-header__home" href="https://tagandtally.quest/" title="Tag & Tally home">
          <img class="sparks-header__logo" :src="logoUrl" alt="Tag & Tally" />
        </a>
        <span class="sparks-header__title">Sparks</span>
      </header>
      <div class="tools-pane">
      <div class="tools-pane__tabs">
        <button
          v-for="tab in APP_TABS"
          :key="tab.name"
          type="button"
          class="tools-pane__tab"
          :class="{ active: activeTab === tab.name }"
          :aria-label="tab.label"
          :title="tab.label"
          @click="setActiveTab(tab.name)"
        >
          <FontAwesomeIcon :icon="['fal', tab.icon]" size="lg" />
        </button>
      </div>
      <div class="tools-pane__content tool-view">
        <ResolutionTab v-if="activeTab === 'resolution'" />
        <SentencesTab v-else-if="activeTab === 'sentences'" />
        <SpellCalculatorTab v-else-if="activeTab === 'spell'" />
        <CardsTab v-else-if="activeTab === 'cards'" />
        <DiceTab v-else-if="activeTab === 'dice'" />
        <RandomTablesTab v-else-if="activeTab === 'tables'" />
      </div>
      <footer class="app-version">v{{ appVersion }}</footer>
    </div>
    </div>
  </div>
</template>

<style scoped>
.app-wrap {
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
}

.app {
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
  height: 100vh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--tt-line);
  border-right: 1px solid var(--tt-line);
}

.sparks-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: var(--tt-panel);
  border-bottom: 2px solid var(--tt-ink);
}

/* Logo is black on white: multiply drops the white onto the panel */
.sparks-header__home {
  display: flex;
}

.sparks-header__logo {
  height: 2.25rem;
  width: auto;
  mix-blend-mode: multiply;
}

.sparks-header__title {
  font-family: var(--tt-font-display);
  font-size: 1.75rem;
  line-height: 1;
  color: var(--tt-blue);
}

.tools-pane {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  background-color: var(--tt-paper);
}

@media (min-width: 1024px) {
  .app {
    max-width: 25%;
  }
  .tools-pane {
    max-width: 100%;
  }
}

.tools-pane__tabs {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.25rem;
  flex-shrink: 0;
  padding: 0 0.5rem;
  background-color: var(--tt-panel);
  border-bottom: 1px solid var(--tt-line);
}

.tools-pane__tab {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  color: var(--tt-ink-soft);
  cursor: pointer;
}
.tools-pane__tab:hover {
  background-color: transparent;
  color: var(--tt-blue);
}
.tools-pane__tab.active {
  background-color: transparent;
  color: var(--tt-blue);
  box-shadow: inset 0 -3px 0 var(--tt-blue);
}
.tools-pane__tab :deep(svg) {
  pointer-events: none;
}
.tools-pane__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0.75rem;
}
.tools-pane__content > * {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.app-version {
  flex-shrink: 0;
  padding: 0.25rem 0.75rem;
  font-family: var(--tt-font-heading);
  letter-spacing: 0.12em;
  font-size: 0.7rem;
  color: var(--text-muted);
  text-align: center;
  background: var(--tt-panel);
  border-top: 1px solid var(--background-modifier-border);
}

.pwa-update-banner {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: var(--background-secondary);
  border: 1px solid var(--background-modifier-border);
  border-bottom: none;
  border-top: 3px solid var(--tt-blue);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
}
.pwa-update-banner__text {
  font-size: 0.9rem;
  color: var(--text-normal);
}
.pwa-update-banner__btn {
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--interactive-accent);
  background: var(--interactive-accent);
  color: var(--text-on-accent);
  font-family: var(--tt-font-heading);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  cursor: pointer;
}
.pwa-update-banner__btn:hover {
  background: var(--interactive-accent-hover);
  border-color: var(--interactive-accent-hover);
}
</style>
